/**
 * MCPBridge Component
 *
 * Mounts window.__GETCLEARANCE__ for AI agent interaction.
 * Provides a control-plane interface: navigation, actions, IDs, UI state.
 *
 * SECURITY: Never exposes PII, auth tokens, or raw API data.
 * Agents must use the authenticated API for actual data access.
 */

import { useEffect, useRef } from 'react';
import { useMCPState } from './useMCPState';
import { useMCPFormState } from './useMCPFormState';
import { useMCPDebug } from './useMCPDebug';
import { createMCPActions } from './mcpActions';
import { AI_CONTEXT } from './aiContext';

export default function MCPBridge() {
  const state = useMCPState();
  const forms = useMCPFormState();
  const debug = useMCPDebug();
  const actionsRef = useRef(null);

  // Create actions once (stable reference)
  useEffect(() => {
    actionsRef.current = createMCPActions({
      navigate: state.navigate,
      queryClient: state.queryClient,
      getToken: state.getToken,
      recordAction: (action, result, error) => {
        // Record to state notifications
        state.recordAction(action, result, error);
        // Also log to debug if enabled
        if (debug.enabled) {
          debug.logAction(action.name, action.args, result, error, action.duration);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.navigate, state.queryClient, state.getToken, state.recordAction, debug.enabled, debug.logAction]);

  // Take periodic state snapshots when debug is enabled
  useEffect(() => {
    if (!debug.enabled) return;

    // Snapshot on route change
    debug.snapshotState();

    const interval = setInterval(() => {
      debug.snapshotState();
    }, 5000); // Snapshot every 5 seconds

    return () => clearInterval(interval);
  }, [debug, state.route.path]);

  // Update window.__GETCLEARANCE__ on every state change
  useEffect(() => {
    const mcpState = {
      __AI_CONTEXT__: AI_CONTEXT,

      ready: state.ready,

      route: state.route,

      queries: state.getQueryMetadata(),

      ui: {
        loading: !!document.querySelector('[data-page-loading="true"]'),
        modals: Array.from(document.querySelectorAll('[data-testid*="modal"]')).map(
          (el) => el.getAttribute('data-testid')
        ),
      },

      notifications: state.notifications,

      actions: actionsRef.current || {},

      waitFor: state.createWaitFor(),

      assert: state.createAssertions(),

      forms,

      debug: {
        enabled: debug.enabled,
        enable: debug.enable,
        disable: debug.disable,
        getActionLog: debug.getActionLog,
        getStateHistory: debug.getStateHistory,
        clear: debug.clear,
        getSummary: debug.getSummary,
        exportLogs: debug.exportLogs,
      },
    };

    window.__GETCLEARANCE__ = mcpState;
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      delete window.__GETCLEARANCE__;
    };
  }, []);

  // This component renders nothing
  return null;
}
